import React, { useState, useMemo } from "react";
import { useBar } from "../../../../context/BarContext";
import useBarArtists from "../../hooks/useBarArtists";
import {
  getCurrentDateTime,
  filterShowsByDateAndTime,
  filterShowsByArtist,
  calculateTotalSales,
  calculatePercentageChange,
  calculateSalesBySlotLabels,
  unionSlotLabels,
  formatCurrency,
  formatPercentage,
  formatDateRange,
  venueWeekRangeYmd,
  venuePreviousWeekPartialRangeYmd,
  venueMonthRangeYmd,
  venuePreviousMonthPartialRangeYmd,
  venueYearRangeYmd,
  venuePreviousYearPartialRangeYmd,
} from "../../utils/financialsMath";
import { normalizeBarTimeZone } from "../../../../utils/civilDateTime";
import { DateTime } from "luxon";

const ByArtist = () => {
  const { selectedBar, barShows, showsLoading } = useBar();
  const { artists, loading: artistsLoading } = useBarArtists(
    selectedBar?.bar_id
  );

  const [mode, setMode] = useState("single"); // 'single' or 'compare'
  const [selectedArtist1, setSelectedArtist1] = useState(null);
  const [selectedArtist2, setSelectedArtist2] = useState(null);

  const timeSlotsRaw = selectedBar?.time_slots;

  const ymdToDateInZone = (ymd, tz) =>
    DateTime.fromISO(ymd, { zone: normalizeBarTimeZone(tz) }).toJSDate();

  // Filter shows by selected artist(s)
  const artist1Shows = useMemo(() => {
    if (!selectedArtist1 || !barShows) return [];
    return filterShowsByArtist(barShows, selectedArtist1);
  }, [barShows, selectedArtist1]);

  const artist2Shows = useMemo(() => {
    if (!selectedArtist2 || !barShows) return [];
    return filterShowsByArtist(barShows, selectedArtist2);
  }, [barShows, selectedArtist2]);

  // Get artist names
  const artist1Name = useMemo(() => {
    if (!selectedArtist1 || !artists) return "";
    const artist = artists.find((a) => a.artist_id === selectedArtist1);
    return artist?.stage_name || "";
  }, [artists, selectedArtist1]);

  const artist2Name = useMemo(() => {
    if (!selectedArtist2 || !artists) return "";
    const artist = artists.find((a) => a.artist_id === selectedArtist2);
    return artist?.stage_name || "";
  }, [artists, selectedArtist2]);

  // Calculate financial data for single artist mode
  const singleArtistData = useMemo(() => {
    const tz = normalizeBarTimeZone(selectedBar?.time_zone);
    const ref = getCurrentDateTime();

    if (!artist1Shows || artist1Shows.length === 0) {
      const emptyBreakdown = {};

      const week = venueWeekRangeYmd(tz, ref);
      const prevWeek = venuePreviousWeekPartialRangeYmd(tz, ref);
      const month = venueMonthRangeYmd(tz, ref);
      const prevMonth = venuePreviousMonthPartialRangeYmd(tz, ref);
      const year = venueYearRangeYmd(tz, ref);
      const prevYear = venuePreviousYearPartialRangeYmd(tz, ref);

      return {
        thisWeek: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(week.startYmd, tz),
            ymdToDateInZone(week.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevWeek.startYmd, tz),
            ymdToDateInZone(prevWeek.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
        thisMonth: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(month.startYmd, tz),
            ymdToDateInZone(month.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevMonth.startYmd, tz),
            ymdToDateInZone(prevMonth.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
        thisYear: {
          sales: 0,
          previousSales: 0,
          change: { percentage: 0, isPositive: true },
          dateRange: formatDateRange(
            ymdToDateInZone(year.startYmd, tz),
            ymdToDateInZone(year.endYmd, tz),
          ),
          previousDateRange: formatDateRange(
            ymdToDateInZone(prevYear.startYmd, tz),
            ymdToDateInZone(prevYear.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: emptyBreakdown,
            previousPeriod: emptyBreakdown,
            comparison: emptyBreakdown,
          },
          slotLabels: [],
        },
      };
    }

    const week = venueWeekRangeYmd(tz, ref);
    const prevWeek = venuePreviousWeekPartialRangeYmd(tz, ref);

    const thisWeekShows = filterShowsByDateAndTime(
      artist1Shows,
      week.startYmd,
      week.endYmd,
      ref,
      tz,
    );
    const previousWeekShows = filterShowsByDateAndTime(
      artist1Shows,
      prevWeek.startYmd,
      prevWeek.endYmd,
      prevWeek.referenceInstant,
      tz,
    );
    const thisWeekSales = calculateTotalSales(thisWeekShows);
    const previousWeekSales = calculateTotalSales(previousWeekShows);
    const weekChange = calculatePercentageChange(thisWeekSales, previousWeekSales);

    const formatBreakdownFromResult = (br) => {
      const formatted = {};
      Object.entries(br.breakdown || {}).forEach(([label, v]) => {
        formatted[label] = v.sales || 0;
      });
      return formatted;
    };

    const thisWeekBreakdown = calculateSalesBySlotLabels(
      thisWeekShows,
      timeSlotsRaw
    );
    const previousWeekBreakdown = calculateSalesBySlotLabels(
      previousWeekShows,
      timeSlotsRaw
    );

    const weekSlotLabels = unionSlotLabels(
      thisWeekBreakdown,
      previousWeekBreakdown
    );

    const weekComparisonBreakdown = {};
    weekSlotLabels.forEach((label) => {
      const thisSales = thisWeekBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousWeekBreakdown.breakdown[label]?.sales || 0;
      weekComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    const month = venueMonthRangeYmd(tz, ref);
    const prevMonth = venuePreviousMonthPartialRangeYmd(tz, ref);

    const thisMonthShows = filterShowsByDateAndTime(
      artist1Shows,
      month.startYmd,
      month.endYmd,
      ref,
      tz,
    );
    const previousMonthShows = filterShowsByDateAndTime(
      artist1Shows,
      prevMonth.startYmd,
      prevMonth.endYmd,
      prevMonth.referenceInstant,
      tz,
    );
    const thisMonthSales = calculateTotalSales(thisMonthShows);
    const previousMonthSales = calculateTotalSales(previousMonthShows);
    const monthChange = calculatePercentageChange(thisMonthSales, previousMonthSales);

    const thisMonthBreakdown = calculateSalesBySlotLabels(
      thisMonthShows,
      timeSlotsRaw
    );
    const previousMonthBreakdown = calculateSalesBySlotLabels(
      previousMonthShows,
      timeSlotsRaw
    );

    const monthSlotLabels = unionSlotLabels(
      thisMonthBreakdown,
      previousMonthBreakdown
    );

    const monthComparisonBreakdown = {};
    monthSlotLabels.forEach((label) => {
      const thisSales = thisMonthBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousMonthBreakdown.breakdown[label]?.sales || 0;
      monthComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    const year = venueYearRangeYmd(tz, ref);
    const prevYear = venuePreviousYearPartialRangeYmd(tz, ref);

    const thisYearShows = filterShowsByDateAndTime(
      artist1Shows,
      year.startYmd,
      year.endYmd,
      ref,
      tz,
    );
    const previousYearShows = filterShowsByDateAndTime(
      artist1Shows,
      prevYear.startYmd,
      prevYear.endYmd,
      prevYear.referenceInstant,
      tz,
    );
    const thisYearSales = calculateTotalSales(thisYearShows);
    const previousYearSales = calculateTotalSales(previousYearShows);
    const yearChange = calculatePercentageChange(thisYearSales, previousYearSales);

    const thisYearBreakdown = calculateSalesBySlotLabels(
      thisYearShows,
      timeSlotsRaw
    );
    const previousYearBreakdown = calculateSalesBySlotLabels(
      previousYearShows,
      timeSlotsRaw
    );

    const yearSlotLabels = unionSlotLabels(
      thisYearBreakdown,
      previousYearBreakdown
    );

    const yearComparisonBreakdown = {};
    yearSlotLabels.forEach((label) => {
      const thisSales = thisYearBreakdown.breakdown[label]?.sales || 0;
      const previousSales = previousYearBreakdown.breakdown[label]?.sales || 0;
      yearComparisonBreakdown[label] = calculatePercentageChange(
        thisSales,
        previousSales
      );
    });

    return {
      thisWeek: {
        sales: thisWeekSales,
        previousSales: previousWeekSales,
        change: weekChange,
        dateRange: formatDateRange(
          ymdToDateInZone(week.startYmd, tz),
          ymdToDateInZone(week.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevWeek.startYmd, tz),
          ymdToDateInZone(prevWeek.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisWeekBreakdown),
          previousPeriod: formatBreakdownFromResult(previousWeekBreakdown),
          comparison: weekComparisonBreakdown,
        },
        slotLabels: weekSlotLabels,
      },
      thisMonth: {
        sales: thisMonthSales,
        previousSales: previousMonthSales,
        change: monthChange,
        dateRange: formatDateRange(
          ymdToDateInZone(month.startYmd, tz),
          ymdToDateInZone(month.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevMonth.startYmd, tz),
          ymdToDateInZone(prevMonth.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisMonthBreakdown),
          previousPeriod: formatBreakdownFromResult(previousMonthBreakdown),
          comparison: monthComparisonBreakdown,
        },
        slotLabels: monthSlotLabels,
      },
      thisYear: {
        sales: thisYearSales,
        previousSales: previousYearSales,
        change: yearChange,
        dateRange: formatDateRange(
          ymdToDateInZone(year.startYmd, tz),
          ymdToDateInZone(year.endYmd, tz),
        ),
        previousDateRange: formatDateRange(
          ymdToDateInZone(prevYear.startYmd, tz),
          ymdToDateInZone(prevYear.endYmd, tz),
        ),
        timeSlotBreakdown: {
          thisPeriod: formatBreakdownFromResult(thisYearBreakdown),
          previousPeriod: formatBreakdownFromResult(previousYearBreakdown),
          comparison: yearComparisonBreakdown,
        },
        slotLabels: yearSlotLabels,
      },
    };
  }, [artist1Shows, timeSlotsRaw, selectedBar?.time_zone]);

  // Calculate financial data for compare mode
  const compareData = useMemo(() => {
    const tz = normalizeBarTimeZone(selectedBar?.time_zone);
    const ref = getCurrentDateTime();

    if (!selectedArtist1 || !selectedArtist2 || !barShows) {
      const emptyBreakdown = {};

      const week = venueWeekRangeYmd(tz, ref);
      const month = venueMonthRangeYmd(tz, ref);
      const year = venueYearRangeYmd(tz, ref);

      return {
        artist1: {
          thisWeek: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(week.startYmd, tz), ymdToDateInZone(week.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
          thisMonth: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(month.startYmd, tz), ymdToDateInZone(month.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
          thisYear: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(year.startYmd, tz), ymdToDateInZone(year.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
        },
        artist2: {
          thisWeek: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(week.startYmd, tz), ymdToDateInZone(week.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
          thisMonth: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(month.startYmd, tz), ymdToDateInZone(month.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
          thisYear: { sales: 0, dateRange: formatDateRange(ymdToDateInZone(year.startYmd, tz), ymdToDateInZone(year.endYmd, tz)), timeSlotBreakdown: { thisPeriod: emptyBreakdown }, slotLabels: [] },
        },
        comparison: {
          thisWeek: { change: { percentage: 0, isPositive: true }, timeSlotBreakdown: { comparison: emptyBreakdown }, slotLabels: [] },
          thisMonth: { change: { percentage: 0, isPositive: true }, timeSlotBreakdown: { comparison: emptyBreakdown }, slotLabels: [] },
          thisYear: { change: { percentage: 0, isPositive: true }, timeSlotBreakdown: { comparison: emptyBreakdown }, slotLabels: [] },
        },
      };
    }

    const week = venueWeekRangeYmd(tz, ref);
    const month = venueMonthRangeYmd(tz, ref);
    const year = venueYearRangeYmd(tz, ref);

    const artist1WeekShows = filterShowsByDateAndTime(
      artist1Shows,
      week.startYmd,
      week.endYmd,
      ref,
      tz,
    );
    const artist2WeekShows = filterShowsByDateAndTime(
      artist2Shows,
      week.startYmd,
      week.endYmd,
      ref,
      tz,
    );

    const artist1WeekSales = calculateTotalSales(artist1WeekShows);
    const artist2WeekSales = calculateTotalSales(artist2WeekShows);
    const weekChange = calculatePercentageChange(artist1WeekSales, artist2WeekSales);

    const formatBreakdownFromResult = (br) => {
      const formatted = {};
      Object.entries(br.breakdown || {}).forEach(([label, v]) => {
        formatted[label] = v.sales || 0;
      });
      return formatted;
    };

    const artist1WeekBreakdown = calculateSalesBySlotLabels(
      artist1WeekShows,
      timeSlotsRaw
    );
    const artist2WeekBreakdown = calculateSalesBySlotLabels(
      artist2WeekShows,
      timeSlotsRaw
    );

    const weekSlotLabels = unionSlotLabels(
      artist1WeekBreakdown,
      artist2WeekBreakdown
    );

    const weekComparisonBreakdown = {};
    weekSlotLabels.forEach((label) => {
      const artist1Sales = artist1WeekBreakdown.breakdown[label]?.sales || 0;
      const artist2Sales = artist2WeekBreakdown.breakdown[label]?.sales || 0;
      weekComparisonBreakdown[label] = calculatePercentageChange(
        artist1Sales,
        artist2Sales
      );
    });

    const artist1MonthShows = filterShowsByDateAndTime(
      artist1Shows,
      month.startYmd,
      month.endYmd,
      ref,
      tz,
    );
    const artist2MonthShows = filterShowsByDateAndTime(
      artist2Shows,
      month.startYmd,
      month.endYmd,
      ref,
      tz,
    );

    const artist1MonthSales = calculateTotalSales(artist1MonthShows);
    const artist2MonthSales = calculateTotalSales(artist2MonthShows);
    const monthChange = calculatePercentageChange(artist1MonthSales, artist2MonthSales);

    const artist1MonthBreakdown = calculateSalesBySlotLabels(
      artist1MonthShows,
      timeSlotsRaw
    );
    const artist2MonthBreakdown = calculateSalesBySlotLabels(
      artist2MonthShows,
      timeSlotsRaw
    );

    const monthSlotLabels = unionSlotLabels(
      artist1MonthBreakdown,
      artist2MonthBreakdown
    );

    const monthComparisonBreakdown = {};
    monthSlotLabels.forEach((label) => {
      const artist1Sales = artist1MonthBreakdown.breakdown[label]?.sales || 0;
      const artist2Sales = artist2MonthBreakdown.breakdown[label]?.sales || 0;
      monthComparisonBreakdown[label] = calculatePercentageChange(
        artist1Sales,
        artist2Sales
      );
    });

    const artist1YearShows = filterShowsByDateAndTime(
      artist1Shows,
      year.startYmd,
      year.endYmd,
      ref,
      tz,
    );
    const artist2YearShows = filterShowsByDateAndTime(
      artist2Shows,
      year.startYmd,
      year.endYmd,
      ref,
      tz,
    );

    const artist1YearSales = calculateTotalSales(artist1YearShows);
    const artist2YearSales = calculateTotalSales(artist2YearShows);
    const yearChange = calculatePercentageChange(artist1YearSales, artist2YearSales);

    const artist1YearBreakdown = calculateSalesBySlotLabels(
      artist1YearShows,
      timeSlotsRaw
    );
    const artist2YearBreakdown = calculateSalesBySlotLabels(
      artist2YearShows,
      timeSlotsRaw
    );

    const yearSlotLabels = unionSlotLabels(
      artist1YearBreakdown,
      artist2YearBreakdown
    );

    const yearComparisonBreakdown = {};
    yearSlotLabels.forEach((label) => {
      const artist1Sales = artist1YearBreakdown.breakdown[label]?.sales || 0;
      const artist2Sales = artist2YearBreakdown.breakdown[label]?.sales || 0;
      yearComparisonBreakdown[label] = calculatePercentageChange(
        artist1Sales,
        artist2Sales
      );
    });

    return {
      artist1: {
        thisWeek: {
          sales: artist1WeekSales,
          dateRange: formatDateRange(
            ymdToDateInZone(week.startYmd, tz),
            ymdToDateInZone(week.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist1WeekBreakdown),
          },
          slotLabels: weekSlotLabels,
        },
        thisMonth: {
          sales: artist1MonthSales,
          dateRange: formatDateRange(
            ymdToDateInZone(month.startYmd, tz),
            ymdToDateInZone(month.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist1MonthBreakdown),
          },
          slotLabels: monthSlotLabels,
        },
        thisYear: {
          sales: artist1YearSales,
          dateRange: formatDateRange(
            ymdToDateInZone(year.startYmd, tz),
            ymdToDateInZone(year.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist1YearBreakdown),
          },
          slotLabels: yearSlotLabels,
        },
      },
      artist2: {
        thisWeek: {
          sales: artist2WeekSales,
          dateRange: formatDateRange(
            ymdToDateInZone(week.startYmd, tz),
            ymdToDateInZone(week.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist2WeekBreakdown),
          },
          slotLabels: weekSlotLabels,
        },
        thisMonth: {
          sales: artist2MonthSales,
          dateRange: formatDateRange(
            ymdToDateInZone(month.startYmd, tz),
            ymdToDateInZone(month.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist2MonthBreakdown),
          },
          slotLabels: monthSlotLabels,
        },
        thisYear: {
          sales: artist2YearSales,
          dateRange: formatDateRange(
            ymdToDateInZone(year.startYmd, tz),
            ymdToDateInZone(year.endYmd, tz),
          ),
          timeSlotBreakdown: {
            thisPeriod: formatBreakdownFromResult(artist2YearBreakdown),
          },
          slotLabels: yearSlotLabels,
        },
      },
      comparison: {
        thisWeek: {
          change: weekChange,
          timeSlotBreakdown: { comparison: weekComparisonBreakdown },
          slotLabels: weekSlotLabels,
        },
        thisMonth: {
          change: monthChange,
          timeSlotBreakdown: { comparison: monthComparisonBreakdown },
          slotLabels: monthSlotLabels,
        },
        thisYear: {
          change: yearChange,
          timeSlotBreakdown: { comparison: yearComparisonBreakdown },
          slotLabels: yearSlotLabels,
        },
      },
    };
  }, [artist1Shows, artist2Shows, timeSlotsRaw, selectedArtist1, selectedArtist2, barShows, selectedBar?.time_zone]);

  // Handle mode change
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === "single") {
      setSelectedArtist2(null);
    }
  };

  // Handle artist selection
  const handleArtist1Change = (artistId) => {
    setSelectedArtist1(artistId ? parseInt(artistId) : null);
    // Prevent selecting same artist in compare mode
    if (mode === "compare" && artistId && parseInt(artistId) === selectedArtist2) {
      setSelectedArtist2(null);
    }
  };

  const handleArtist2Change = (artistId) => {
    setSelectedArtist2(artistId ? parseInt(artistId) : null);
    // Prevent selecting same artist
    if (artistId && parseInt(artistId) === selectedArtist1) {
      setSelectedArtist1(null);
    }
  };

  if (showsLoading || artistsLoading) {
    return (
      <div className="financials-by-artist">
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (!selectedBar) {
    return (
      <div className="financials-by-artist">
        <div className="no-data-message">
          <p>Please select a bar to view financials.</p>
        </div>
      </div>
    );
  }

  if (!artists || artists.length === 0) {
    return (
      <div className="financials-by-artist">
        <div className="no-data-message">
          <p>No artists associated with this bar.</p>
        </div>
      </div>
    );
  }

  // Render single artist mode
  if (mode === "single") {
    return (
      <div className="financials-by-artist">
        {/* Controls */}
        <div className="artist-controls">
          <div className="mode-toggle">
            <button
              className={`mode-toggle-btn ${mode === "single" ? "active" : ""}`}
              onClick={() => handleModeChange("single")}
            >
              Single Artist
            </button>
            <button
              className={`mode-toggle-btn ${mode === "compare" ? "active" : ""}`}
              onClick={() => handleModeChange("compare")}
            >
              Compare Artists
            </button>
          </div>
          <div className="artist-selector">
            <label htmlFor="artist-select">Select Artist:</label>
            <select
              id="artist-select"
              value={selectedArtist1 || ""}
              onChange={(e) => handleArtist1Change(e.target.value)}
              className="artist-dropdown"
            >
              <option value="">-- Select Artist --</option>
              {artists.map((artist) => (
                <option key={artist.artist_id} value={artist.artist_id}>
                  {artist.stage_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!selectedArtist1 ? (
          <div className="no-data-message">
            <p>Please select an artist to view financials.</p>
          </div>
        ) : (
          <>
            {/* Original Summary Cards */}
            <div className="financial-grid">
              <div className="financial-card">
                <span
                  className="info-icon card-info-icon"
                  data-tooltip="Shows total sales from Monday of this week through today for the selected artist. Only includes shows that have already ended. The change percentage compares this week's sales to the same period last week."
                >
                  ℹ️
                </span>
                <h3>This Week</h3>
                <div className="date-range">{singleArtistData.thisWeek.dateRange}</div>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(singleArtistData.thisWeek.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Change</span>
                    <span
                      className={`slot-amount ${
                        singleArtistData.thisWeek.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        singleArtistData.thisWeek.change.percentage,
                        singleArtistData.thisWeek.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <span
                  className="info-icon card-info-icon"
                  data-tooltip="Shows total sales from the 1st of this month through today for the selected artist. Only includes shows that have already ended. The change percentage compares this month's sales to the same period last month."
                >
                  ℹ️
                </span>
                <h3>This Month</h3>
                <div className="date-range">{singleArtistData.thisMonth.dateRange}</div>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(singleArtistData.thisMonth.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Change</span>
                    <span
                      className={`slot-amount ${
                        singleArtistData.thisMonth.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        singleArtistData.thisMonth.change.percentage,
                        singleArtistData.thisMonth.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <span
                  className="info-icon card-info-icon"
                  data-tooltip="Shows total sales from January 1st of this year through today for the selected artist. Only includes shows that have already ended. The change percentage compares this year's sales to the same period last year."
                >
                  ℹ️
                </span>
                <h3>This Year</h3>
                <div className="date-range">{singleArtistData.thisYear.dateRange}</div>
                <div className="earnings-breakdown">
                  <div className="time-slot">
                    <span className="slot-label">Sales</span>
                    <span className="slot-amount sales">
                      {formatCurrency(singleArtistData.thisYear.sales)}
                    </span>
                  </div>
                  <div className="time-slot total">
                    <span className="slot-label">Change</span>
                    <span
                      className={`slot-amount ${
                        singleArtistData.thisYear.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        singleArtistData.thisYear.change.percentage,
                        singleArtistData.thisYear.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Week Comparison Section */}
            <div className="comparison-section-box">
              <span
                className="info-icon section-info-icon"
                data-tooltip="Compares this week's sales to last week's sales for the selected artist, broken down by time slot. Previous Period shows last week's sales by time slot. This Period shows this week's sales by time slot. Comparison shows the percentage change for each time slot and overall."
              >
                ℹ️
              </span>
              <h2 className="comparison-section-title">Week Comparison</h2>
              <div className="financial-grid">
                <div className="financial-card">
                  <h3>Previous Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisWeek.previousDateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisWeek.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisWeek.timeSlotBreakdown.previousPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisWeek.previousSales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>This Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisWeek.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisWeek.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisWeek.timeSlotBreakdown.thisPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisWeek.sales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>Comparison</h3>
                  <div className="date-range">
                    {singleArtistData.thisWeek.previousDateRange} vs{" "}
                    {singleArtistData.thisWeek.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisWeek.slotLabels.map((label) => {
                      const comparison =
                        singleArtistData.thisWeek.timeSlotBreakdown.comparison[
                          label
                        ] || { percentage: 0, isPositive: true };
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span
                            className={`slot-amount ${
                              comparison.isPositive
                                ? "profit-positive"
                                : "profit-negative"
                            }`}
                          >
                            {formatPercentage(
                              comparison.percentage,
                              comparison.isPositive
                            )}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Overall:</span>
                      <span
                        className={`slot-amount ${
                          singleArtistData.thisWeek.change.isPositive
                            ? "profit-positive"
                            : "profit-negative"
                        }`}
                      >
                        {formatPercentage(
                          singleArtistData.thisWeek.change.percentage,
                          singleArtistData.thisWeek.change.isPositive
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Month Comparison Section */}
            <div className="comparison-section-box">
              <span
                className="info-icon section-info-icon"
                data-tooltip="Compares this month's sales to last month's sales for the selected artist, broken down by time slot. Previous Period shows last month's sales by time slot. This Period shows this month's sales by time slot. Comparison shows the percentage change for each time slot and overall."
              >
                ℹ️
              </span>
              <h2 className="comparison-section-title">Month Comparison</h2>
              <div className="financial-grid">
                <div className="financial-card">
                  <h3>Previous Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisMonth.previousDateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisMonth.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisMonth.timeSlotBreakdown.previousPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisMonth.previousSales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>This Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisMonth.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisMonth.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisMonth.timeSlotBreakdown.thisPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisMonth.sales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>Comparison</h3>
                  <div className="date-range">
                    {singleArtistData.thisMonth.previousDateRange} vs{" "}
                    {singleArtistData.thisMonth.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisMonth.slotLabels.map((label) => {
                      const comparison =
                        singleArtistData.thisMonth.timeSlotBreakdown.comparison[
                          label
                        ] || { percentage: 0, isPositive: true };
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span
                            className={`slot-amount ${
                              comparison.isPositive
                                ? "profit-positive"
                                : "profit-negative"
                            }`}
                          >
                            {formatPercentage(
                              comparison.percentage,
                              comparison.isPositive
                            )}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Overall:</span>
                      <span
                        className={`slot-amount ${
                          singleArtistData.thisMonth.change.isPositive
                            ? "profit-positive"
                            : "profit-negative"
                        }`}
                      >
                        {formatPercentage(
                          singleArtistData.thisMonth.change.percentage,
                          singleArtistData.thisMonth.change.isPositive
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Year Comparison Section */}
            <div className="comparison-section-box">
              <span
                className="info-icon section-info-icon"
                data-tooltip="Compares this year's sales to last year's sales for the selected artist, broken down by time slot. Previous Period shows last year's sales by time slot (from January 1st to the same date last year). This Period shows this year's sales by time slot (from January 1st to today). Comparison shows the percentage change for each time slot and overall."
              >
                ℹ️
              </span>
              <h2 className="comparison-section-title">Year Comparison</h2>
              <div className="financial-grid">
                <div className="financial-card">
                  <h3>Previous Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisYear.previousDateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisYear.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisYear.timeSlotBreakdown.previousPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisYear.previousSales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>This Period</h3>
                  <div className="date-range">
                    {singleArtistData.thisYear.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisYear.slotLabels.map((label) => {
                      const sales =
                        singleArtistData.thisYear.timeSlotBreakdown.thisPeriod[
                          label
                        ] || 0;
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span className="slot-amount sales">
                            {formatCurrency(sales)}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Total:</span>
                      <span className="slot-amount sales">
                        {formatCurrency(singleArtistData.thisYear.sales)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="financial-card">
                  <h3>Comparison</h3>
                  <div className="date-range">
                    {singleArtistData.thisYear.previousDateRange} vs{" "}
                    {singleArtistData.thisYear.dateRange}
                  </div>
                  <div className="earnings-breakdown">
                    {singleArtistData.thisYear.slotLabels.map((label) => {
                      const comparison =
                        singleArtistData.thisYear.timeSlotBreakdown.comparison[
                          label
                        ] || { percentage: 0, isPositive: true };
                      return (
                        <div key={label} className="time-slot">
                          <span className="slot-label">{label}:</span>
                          <span
                            className={`slot-amount ${
                              comparison.isPositive
                                ? "profit-positive"
                                : "profit-negative"
                            }`}
                          >
                            {formatPercentage(
                              comparison.percentage,
                              comparison.isPositive
                            )}
                          </span>
                        </div>
                      );
                    })}
                    <div className="time-slot total">
                      <span className="slot-label">Overall:</span>
                      <span
                        className={`slot-amount ${
                          singleArtistData.thisYear.change.isPositive
                            ? "profit-positive"
                            : "profit-negative"
                        }`}
                      >
                        {formatPercentage(
                          singleArtistData.thisYear.change.percentage,
                          singleArtistData.thisYear.change.isPositive
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Render compare mode
  return (
    <div className="financials-by-artist">
      {/* Controls */}
      <div className="artist-controls">
        <div className="mode-toggle">
          <button
            className={`mode-toggle-btn ${mode === "single" ? "active" : ""}`}
            onClick={() => handleModeChange("single")}
          >
            Single Artist
          </button>
          <button
            className={`mode-toggle-btn ${mode === "compare" ? "active" : ""}`}
            onClick={() => handleModeChange("compare")}
          >
            Compare Artists
          </button>
        </div>
        <div className="artist-selectors">
          <div className="artist-selector">
            <label htmlFor="artist1-select">Artist 1:</label>
            <select
              id="artist1-select"
              value={selectedArtist1 || ""}
              onChange={(e) => handleArtist1Change(e.target.value)}
              className="artist-dropdown"
            >
              <option value="">-- Select Artist 1 --</option>
              {artists.map((artist) => (
                <option
                  key={artist.artist_id}
                  value={artist.artist_id}
                  disabled={artist.artist_id === selectedArtist2}
                >
                  {artist.stage_name}
                </option>
              ))}
            </select>
          </div>
          <div className="artist-selector">
            <label htmlFor="artist2-select">Artist 2:</label>
            <select
              id="artist2-select"
              value={selectedArtist2 || ""}
              onChange={(e) => handleArtist2Change(e.target.value)}
              className="artist-dropdown"
            >
              <option value="">-- Select Artist 2 --</option>
              {artists.map((artist) => (
                <option
                  key={artist.artist_id}
                  value={artist.artist_id}
                  disabled={artist.artist_id === selectedArtist1}
                >
                  {artist.stage_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!selectedArtist1 || !selectedArtist2 ? (
        <div className="no-data-message">
          <p>Please select both artists to compare.</p>
        </div>
      ) : (
        <>
          {/* Week Comparison Section */}
          <div className="comparison-section-box">
            <span
              className="info-icon section-info-icon"
              data-tooltip="Compares two artists' sales for this week, broken down by time slot. Artist 1 shows this week's sales by time slot. Artist 2 shows this week's sales by time slot. Comparison shows the percentage difference (Artist 1 vs Artist 2) for each time slot and overall."
            >
              ℹ️
            </span>
            <h2 className="comparison-section-title">Week Comparison</h2>
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Artist 1: {artist1Name}</h3>
                <div className="date-range">
                  {compareData.artist1.thisWeek.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist1.thisWeek.slotLabels.map((label) => {
                    const sales =
                      compareData.artist1.thisWeek.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist1.thisWeek.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Artist 2: {artist2Name}</h3>
                <div className="date-range">
                  {compareData.artist2.thisWeek.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist2.thisWeek.slotLabels.map((label) => {
                    const sales =
                      compareData.artist2.thisWeek.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist2.thisWeek.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Comparison</h3>
                <div className="date-range">
                  {compareData.artist1.thisWeek.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.comparison.thisWeek.slotLabels.map((label) => {
                    const comparison =
                      compareData.comparison.thisWeek.timeSlotBreakdown.comparison[
                        label
                      ] || { percentage: 0, isPositive: true };
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span
                          className={`slot-amount ${
                            comparison.isPositive
                              ? "profit-positive"
                              : "profit-negative"
                          }`}
                        >
                          {formatPercentage(
                            comparison.percentage,
                            comparison.isPositive
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Overall:</span>
                    <span
                      className={`slot-amount ${
                        compareData.comparison.thisWeek.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        compareData.comparison.thisWeek.change.percentage,
                        compareData.comparison.thisWeek.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Month Comparison Section */}
          <div className="comparison-section-box">
            <span
              className="info-icon section-info-icon"
              data-tooltip="Compares two artists' sales for this month, broken down by time slot. Artist 1 shows this month's sales by time slot. Artist 2 shows this month's sales by time slot. Comparison shows the percentage difference (Artist 1 vs Artist 2) for each time slot and overall."
            >
              ℹ️
            </span>
            <h2 className="comparison-section-title">Month Comparison</h2>
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Artist 1: {artist1Name}</h3>
                <div className="date-range">
                  {compareData.artist1.thisMonth.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist1.thisMonth.slotLabels.map((label) => {
                    const sales =
                      compareData.artist1.thisMonth.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist1.thisMonth.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Artist 2: {artist2Name}</h3>
                <div className="date-range">
                  {compareData.artist2.thisMonth.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist2.thisMonth.slotLabels.map((label) => {
                    const sales =
                      compareData.artist2.thisMonth.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist2.thisMonth.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Comparison</h3>
                <div className="date-range">
                  {compareData.artist1.thisMonth.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.comparison.thisMonth.slotLabels.map((label) => {
                    const comparison =
                      compareData.comparison.thisMonth.timeSlotBreakdown.comparison[
                        label
                      ] || { percentage: 0, isPositive: true };
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span
                          className={`slot-amount ${
                            comparison.isPositive
                              ? "profit-positive"
                              : "profit-negative"
                          }`}
                        >
                          {formatPercentage(
                            comparison.percentage,
                            comparison.isPositive
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Overall:</span>
                    <span
                      className={`slot-amount ${
                        compareData.comparison.thisMonth.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        compareData.comparison.thisMonth.change.percentage,
                        compareData.comparison.thisMonth.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Year Comparison Section */}
          <div className="comparison-section-box">
            <span
              className="info-icon section-info-icon"
              data-tooltip="Compares two artists' sales for this year, broken down by time slot. Artist 1 shows this year's sales by time slot (from January 1st to today). Artist 2 shows this year's sales by time slot (from January 1st to today). Comparison shows the percentage difference (Artist 1 vs Artist 2) for each time slot and overall."
            >
              ℹ️
            </span>
            <h2 className="comparison-section-title">Year Comparison</h2>
            <div className="financial-grid">
              <div className="financial-card">
                <h3>Artist 1: {artist1Name}</h3>
                <div className="date-range">
                  {compareData.artist1.thisYear.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist1.thisYear.slotLabels.map((label) => {
                    const sales =
                      compareData.artist1.thisYear.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist1.thisYear.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Artist 2: {artist2Name}</h3>
                <div className="date-range">
                  {compareData.artist2.thisYear.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.artist2.thisYear.slotLabels.map((label) => {
                    const sales =
                      compareData.artist2.thisYear.timeSlotBreakdown.thisPeriod[
                        label
                      ] || 0;
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span className="slot-amount sales">
                          {formatCurrency(sales)}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Total:</span>
                    <span className="slot-amount sales">
                      {formatCurrency(compareData.artist2.thisYear.sales)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="financial-card">
                <h3>Comparison</h3>
                <div className="date-range">
                  {compareData.artist1.thisYear.dateRange}
                </div>
                <div className="earnings-breakdown">
                  {compareData.comparison.thisYear.slotLabels.map((label) => {
                    const comparison =
                      compareData.comparison.thisYear.timeSlotBreakdown.comparison[
                        label
                      ] || { percentage: 0, isPositive: true };
                    return (
                      <div key={label} className="time-slot">
                        <span className="slot-label">{label}:</span>
                        <span
                          className={`slot-amount ${
                            comparison.isPositive
                              ? "profit-positive"
                              : "profit-negative"
                          }`}
                        >
                          {formatPercentage(
                            comparison.percentage,
                            comparison.isPositive
                          )}
                        </span>
                      </div>
                    );
                  })}
                  <div className="time-slot total">
                    <span className="slot-label">Overall:</span>
                    <span
                      className={`slot-amount ${
                        compareData.comparison.thisYear.change.isPositive
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatPercentage(
                        compareData.comparison.thisYear.change.percentage,
                        compareData.comparison.thisYear.change.isPositive
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ByArtist;
